<script setup lang="ts" generic="T extends Record<string, any>">
	import type { TableColumnsType } from 'ant-design-vue'
	import type { ClassNameValue } from 'tailwind-merge'
	import type { RecordsFn } from '~/composables/usePaginator'
	import { usePaginator } from '~/composables/usePaginator'
	import { resolve } from '~/utils/functions'
	import { useDebounceFn } from '@vueuse/core'
	import type { RecordSchema } from '@/types'

	interface MinimalColumn {
		key?: string | number
		dataIndex?: string | number | (string | number)[]
		title?: unknown
		name?: string
		width?: string | number | undefined
		hidden?: boolean
	}

	const props = withDefaults(
		defineProps<{
			columns: TableColumnsType
			records: RecordsFn<T>
			loading?: boolean
			showLimit?: boolean
			filterWrapClass?: ClassNameValue
			options?: TableFilterOptions
			tableClass?: string | string[] | Record<string, boolean>
			/**
			 * @description This is used to cache the table data to a unique key.
			 */
			cacheKey?: string
			/**
			 * @description This is used to store the table column to the localStorage.
			 */
			storeKey?: string
			scroll?: { y?: string | number; x?: string | number }
			selectedItems?: (string | number)[]
			/**
			 * @description This is used to set the unique key of the table data. This is important for checkboxes.
			 * @default - "id"
			 */
			uniqueKey?: string
			/**
			 * @description This is used to enable the search of the table data.
			 * @default false
			 */
			enableSearch?: boolean
			/**
			 * @description This is used to enable the sort of the table data.
			 * @default true
			 */
			enableSort?: boolean
			searchPlaceholder?: string
			/**
			 * @description This is used to enable the drag of the table columns.
			 * @default true
			 */
			enableDrag?: boolean
			/**
			 * @description This is used to enable the export of the table data.
			 * @default false
			 */
			enableExport?: boolean
			/**
			 * @description This is used to enable the filter of the table data.
			 * @default true
			 */
			enableFilter?: boolean
			/**
			 * @description This is used to fix the columns in the table.
			 * @default ['details', 'sn', 'checkbox', 'action', 'customer', 'customer_name']
			 * These columns will not be hidden by users
			 * */
			fixedColumnKeys?: string[]
			/**
			 * @description This is used to export the table data.
			 * @returns {Promise<any>} - The table data.
			 */
			export?: () => Promise<unknown>
			/**
			 * @description This is used to enable the refresh of the table data.
			 * @default true
			 */
			enableRefresh?: boolean
			/**
			 * @description This is where we set the name of the exported file.
			 * @default - "Table Export"
			 */
			exportName?: string
			/**
			 * @description This is used to transform the table data.
			 * @returns {any} - The table data.
			 */
			transformRecord?: (record: T) => T
		}>(),
		{
			showLimit: true,
			options: () => ({ pagination: true }),
			selectedItems: () => [],
			uniqueKey: 'id',
			enableSort: false,
			searchPlaceholder: 'Search…',
			enableDrag: false,
			enableExport: false,
			enableFilter: true,
			enableRefresh: true
		}
	)

	const emit = defineEmits<{
		(e: 'load', meta: PaginationContext<T>): void
		(e: 'pageChange', page: number): void
		(e: 'update:selectedItems', items: (string | number)[]): void
		(e: 'select', record: T, checked: boolean): void
		(e: 'selectAll', records: T[], checked: boolean): void
	}>()

	const route = useRoute()
	const router = useRouter()

	const { downloadBlob } = useUtils()
	const { format } = useCurrency()

	const { loadDraft, updateDraft } = useDraft<{
		currentColumns: TableColumn[]
		knownColumnKeys?: string[]
		sortConfig: { key: string | null; order: 'asc' | 'desc' }
	}>(props.storeKey ?? '')

	const displayColumns = ref<TableColumn[]>([])
	const immutableColumns = ref<string[]>(['details', 'sn', 'checkbox', 'action', 'customer', 'customer_name'])
	const sortConfig = ref<{ key: string | null; order: 'asc' | 'desc' }>({
		key: null,
		order: 'asc'
	})
	const getColKey = (c: unknown): string => {
		const col = c as MinimalColumn
		const key = col.key || col.dataIndex
		if (Array.isArray(key)) return key.join('.')
		return String(key || '')
	}

	const getColIndex = (c: unknown): number => {
		const col = c as MinimalColumn
		const key = getColKey(col)
		return (displayColumns.value as unknown as MinimalColumn[]).findIndex(c => getColKey(c) === key)
	}

	const parseWidth = (w: string | number | undefined): number => {
		if (typeof w === 'number') return w
		if (typeof w === 'string') {
			const parsed = parseInt(w.replace('px', ''), 10)
			return isNaN(parsed) ? 150 : parsed
		}
		return 150
	}

	const transformToTableColumn = (columns: MinimalColumn[]): TableColumn[] => {
		return (columns || []).map(c => {
			const titleText = (
				typeof c.title === 'string' ? c.title : c.name || (c as { title?: string }).title || ''
			) as string
			const col: Record<string, unknown> = {
				key: getColKey(c),
				name: titleText,
				title: titleText,
				hidden: !!c.hidden
			}
			// Only set width if explicitly specified — prevents ant-table from
			// generating a fixed-width <col> element for every column.
			if (c.width !== undefined) {
				col.width = typeof c.width === 'number' ? `${c.width}px` : String(c.width)
			}
			return col
		})
	}

	const availableTableColumns = computed(() => transformToTableColumn(props.columns as unknown as MinimalColumn[]))
	const currentTableColumns = computed(() =>
		transformToTableColumn(displayColumns.value as unknown as MinimalColumn[]).filter(c => !c.hidden)
	)

	const tableKey = computed(() => {
		const colKeys = (displayColumns.value as unknown as MinimalColumn[]).map(c => getColKey(c)).join('-')
		return `${colKeys}-${sortConfig.value.key}-${sortConfig.value.order}`
	})
	const saveState = async (currentCol: TableColumn[]) => {
		if (props.storeKey) {
			await updateDraft({
				currentColumns: currentCol,
				knownColumnKeys: availableTableColumns.value.map(c => String(c.key)),
				sortConfig: sortConfig.value
			})
		}
	}

	const extractColumnsFromStorage = (storedColumns: TableColumn[]) => {
		// Storage extraction
		let transformedToStringArray: string[] = []
		if (storedColumns.length && storedColumns.every(item => item?.key)) {
			transformedToStringArray = storedColumns.map(item => (!item.hidden ? String(item.key) : '')).filter(Boolean)
		}

		const list = transformedToStringArray
			.map(key => (props.columns.find(column => String(column.key) === key) || {}) as MinimalColumn)
			.filter(Boolean) as typeof props.columns

		displayColumns.value = list as unknown as TableColumn[]
	}

	watch(
		() => props.columns,
		columns => extractColumnsFromStorage(loadDraft.value.currentColumns || columns),
		{ immediate: true, deep: true }
	)

	// Load sort config from draft and URL
	watch(
		loadDraft,
		draft => {
			const queryKey = route.query.sort_key as string
			const queryOrder = route.query.sort_order as 'asc' | 'desc'

			if (queryKey && queryOrder) {
				sortConfig.value = { key: queryKey, order: queryOrder }
			} else if (draft?.sortConfig) {
				sortConfig.value = draft.sortConfig
			}
		},
		{ immediate: true }
	)

	const debouncedSortSync = useDebounceFn((config: { key: string | null; order: 'asc' | 'desc' }) => {
		router.replace({
			query: {
				...route.query,
				sort_key: config.key || undefined,
				sort_order: config.key ? config.order : undefined
			}
		})
	}, 300)

	watch(
		sortConfig,
		config => {
			debouncedSortSync(config)
		},
		{ deep: true }
	)

	const handleSort = async (key: string) => {
		if (immutableColumns.value.includes(key)) return

		if (sortConfig.value.key === key) {
			sortConfig.value.order = sortConfig.value.order === 'asc' ? 'desc' : 'asc'
		} else {
			sortConfig.value.key = key
			sortConfig.value.order = 'asc'
		}
		await saveState(displayColumns.value)
	}

	const getNestedValue = (obj: Record<string, unknown> | null | undefined, key: string): unknown => {
		if (!obj || !key) return null
		if (key === 'customer_name')
			return (
				(obj.customer as Record<string, unknown>)?.name ||
				(obj.customer as Record<string, unknown>)?.email ||
				null
			)
		if (key === 'plan_name') return (obj.product as Record<string, unknown>)?.name || null

		const parts = key.split('.')
		let current: unknown = obj
		for (const part of parts) {
			if (current == null) return null
			current = (current as Record<string, unknown>)[part]
		}
		return current
	}

	const resizing = ref<{ index: number; startX: number; startWidth: number } | null>(null)

	const startResize = (e: MouseEvent, index: number) => {
		const col = displayColumns.value[index]
		if (!col) return

		resizing.value = {
			index,
			startX: e.pageX,
			startWidth: parseWidth(col.width)
		}
		document.addEventListener('mousemove', onMouseMove)
		document.addEventListener('mouseup', stopResize)
		e.stopPropagation()
		e.preventDefault()
	}

	const onMouseMove = (e: MouseEvent) => {
		if (!resizing.value) return
		const delta = e.pageX - resizing.value.startX
		const newWidth = Math.max(50, resizing.value.startWidth + delta)
		const col = displayColumns.value[resizing.value.index]
		if (col) {
			col.width = newWidth
		}
	}

	const stopResize = () => {
		resizing.value = null
		document.removeEventListener('mousemove', onMouseMove)
		document.removeEventListener('mouseup', stopResize)
		saveState(displayColumns.value)
	}

	let dragSourceIndex: number | null = null
	const dragOverIndex = ref<number | null>(null)

	const onDragStart = (e: DragEvent, index: number) => {
		dragSourceIndex = index
		if (e.dataTransfer) {
			e.dataTransfer.effectAllowed = 'move'
			e.dataTransfer.setData('text/plain', index.toString())
		}
	}

	const onDragOver = (e: DragEvent, index: number) => {
		e.preventDefault()
		if (dragSourceIndex === null) return
		dragOverIndex.value = index
	}

	const onDrop = async (e: DragEvent, targetIndex: number) => {
		e.preventDefault()
		if (dragSourceIndex === null || targetIndex === dragSourceIndex) {
			dragOverIndex.value = null
			dragSourceIndex = null
			return
		}

		const cols = [...(displayColumns.value as unknown as MinimalColumn[])]
		const [moved] = cols.splice(dragSourceIndex, 1)
		if (moved) {
			cols.splice(targetIndex, 0, moved)
		}

		extractColumnsFromStorage(cols as unknown as TableColumn[])
		dragOverIndex.value = null
		dragSourceIndex = null
		await saveState(cols as unknown as TableColumn[])
	}

	const optionsRef = computed(() => props.options)
	const searchQuery = ref((route.query.search as string) || '')
	const { urlQuery } = useUtils()
	const handleSearch = () => {
		router.replace({
			query: {
				...urlQuery.value,
				search: searchQuery.value || undefined,
				page: undefined
			}
		})
	}

	const mergedOptions = computed<TableFilterOptions<RecordSchema>>(() => ({
		pagination: true,
		...optionsRef.value,
		filter: {
			...(optionsRef.value?.filter || {}),
			...(props.enableSearch && route.query.search
				? { search: searchQuery.value || String(route.query.search) }
				: {})
		}
	}))

	const {
		tableRecord,
		paginationQuery,
		paginationMeta,
		currentPage,
		isFetching,
		limit,
		switchPages,
		refreshRequest
	} = usePaginator<T>({
		records: props.records,
		options: mergedOptions,
		cacheKey: () => props.cacheKey,
		transformRecord: props.transformRecord,
		onLoad: data => emit('load', data)
	})

	const sortedRecords = computed(() => {
		const data = [...tableRecord.value]
		if (!sortConfig.value.key) return data

		const { key, order } = sortConfig.value
		return data.sort((a, b) => {
			const valA = getNestedValue(a, key)
			const valB = getNestedValue(b, key)

			if (valA === valB) return 0
			if (valA == null) return 1
			if (valB == null) return -1

			const dateA = Date.parse(valA as string)
			const dateB = Date.parse(valB as string)
			const isDate = !isNaN(dateA) && !isNaN(dateB) && String(valA).includes('-') && String(valB).includes('-')

			let comparison = 0
			if (isDate) {
				comparison = dateA < dateB ? -1 : 1
			} else {
				comparison = String(valA).localeCompare(String(valB), undefined, { numeric: true, sensitivity: 'base' })
			}

			return order === 'asc' ? comparison : -comparison
		})
	})

	const isLoading = computed(() => props.loading || isFetching.value)
	const isInitiallyEmpty = computed(
		() =>
			!isFetching.value &&
			currentPage.value <= 1 &&
			tableRecord.value.length === 0 &&
			!mergedOptions.value?.filter?.search
	)

	const showPagination = computed(
		() => (props.options?.pagination ?? true) && Number(paginationMeta.value.total_pages) > 1
	)
	const paginationJustify = computed(() => (props.showLimit ? 'justify-between' : 'justify-end'))

	const handleToggleSelection = (record: T, checked: boolean) => {
		const items = [...props.selectedItems]
		const val = record[props.uniqueKey]
		if (checked) {
			const exists = items.some(id => String(id) === String(val))
			if (!exists) items.push(val)
		} else {
			const index = items.findIndex(id => String(id) === String(val))
			if (index > -1) items.splice(index, 1)
		}
		emit('update:selectedItems', items)
		emit('select', record, checked)
	}

	const isExporting = defineModel<boolean>('exporting', { default: false })
	const handleExport = async () => {
		await resolve(async () => {
			const response = props.export
				? await props.export()
				: await props.records({ ...(mergedOptions.value.filter || {}), is_export: true })

			const resData = (response as IRequestResponse<unknown>)?.data
			const blob = response instanceof Blob ? response : resData instanceof Blob ? resData : null

			if (blob) {
				const fileName = props.exportName || props.storeKey || props.cacheKey || 'export'
				const extension = blob.type.includes('spreadsheet') || blob.type.includes('excel') ? '.xlsx' : '.csv'
				downloadBlob(blob, { name: fileName, extension })
			}
		}, isExporting)
	}

	const handleToggleAllSelection = (checked: boolean) => {
		const items = [...props.selectedItems]
		if (checked) {
			tableRecord.value.forEach(record => {
				const val = record[props.uniqueKey]
				const exists = items.some(id => String(id) === String(val))
				if (!exists) {
					items.push(val)
				}
			})
		} else {
			tableRecord.value.forEach(record => {
				const val = record[props.uniqueKey]
				const index = items.findIndex(id => String(id) === String(val))
				if (index > -1) {
					items.splice(index, 1)
				}
			})
		}
		emit('update:selectedItems', items)
		emit('selectAll', tableRecord.value as T[], checked)
	}

	const isAllSelected = computed(() => {
		if (tableRecord.value.length === 0) return false
		return tableRecord.value.every(record => isRecordSelected(record))
	})

	const isIndeterminate = computed(() => {
		if (tableRecord.value.length === 0) return false
		const selectedCount = tableRecord.value.filter(record => isRecordSelected(record)).length
		return selectedCount > 0 && selectedCount < tableRecord.value.length
	})
	const list = computed(() => (cellProps: Record<string, unknown>) => cellProps.record as T & { key: number })

	const isRecordSelected = (record: T) => {
		const val = record[props.uniqueKey]
		return props.selectedItems.some(id => String(id) === String(val))
	}

	defineExpose({
		paginationMeta,
		currentPage,
		tableRecord,
		totalCount: computed(() => paginationMeta.value?.total_count),
		refreshRequest,
		handleExport
	})
</script>

<template>
	<div class="paginated-table relative">
		<div
			v-if="enableFilter"
			:class="cn('flex flex-wrap items-center justify-between gap-4 px-5 py-5', filterWrapClass)"
		>
			<slot name="filter-section">
				<div class="flex items-center gap-2">
					<slot name="filter-left">
						<form-kit-search
							v-if="enableSearch && $slots?.['filter-right']"
							v-model="searchQuery"
							:placeholder="searchPlaceholder"
							@search="handleSearch"
							@clear="handleSearch"
						/>
					</slot>
				</div>
				<div class="flex items-center gap-4">
					<slot name="filter-right">
						<form-kit-search
							v-if="enableSearch"
							v-model="searchQuery"
							:placeholder="searchPlaceholder"
							@search="handleSearch"
							@clear="handleSearch"
						/>
					</slot>

					<ui-dropdown v-if="enableSort" dropdown-classes="min-w-[220px]">
						<template #trigger>
							<ui-button
								variants="two"
								class="h-auto rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-xs font-medium hover:border-primary-400"
							>
								<template #prefix>
									<tools-icon dir="arrows" icon="sort" :height="16" />
								</template>
								Sort by:
								<span class="ml-1 capitalize text-primary-2">{{
									sortConfig.key?.replaceAll('_', ' ') || 'None'
								}}</span>
							</ui-button>
						</template>

						<template #default="{ toggle }">
							<div class="relative z-[9999] flex max-h-[400px] min-w-[220px] flex-col">
								<div
									class="mb-1 border-b border-gray-200 px-3 py-2 text-xs font-semibold uppercase tracking-widest text-gray-600"
								>
									Sort Results
								</div>
								<div class="overflow-y-auto">
									<div
										v-for="col in (displayColumns as unknown as MinimalColumn[]).filter(
											c => !immutableColumns.includes(getColKey(c))
										)"
										:key="getColKey(col)"
										class="flex cursor-pointer items-center justify-between rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-50"
										:class="{ 'bg-primary-50 text-sky-600': sortConfig.key === getColKey(col) }"
										@click="() => [handleSort(getColKey(col)), toggle()]"
									>
										<span class="text-sm font-medium text-gray-500">{{
											col.title || (col as any).name
										}}</span>
										<tools-icon
											v-if="sortConfig.key === getColKey(col)"
											dir="arrows"
											:icon="sortConfig.order === 'asc' ? 'arrow-up' : 'arrow-down'"
											:height="14"
										/>
									</div>
								</div>
								<div
									class="mt-1 border-t pt-1"
									@click="() => [(sortConfig = { key: null, order: 'asc' }), toggle()]"
								>
									<div
										class="cursor-pointer rounded-lg px-3 py-2 text-center text-xs font-semibold text-red-500 hover:bg-red-50"
									>
										Clear sorting
									</div>
								</div>
							</div>
						</template>
					</ui-dropdown>

					<ui-button
						v-if="enableExport"
						class="h-auto rounded-lg border border-primary-300 bg-primary-2 px-4 py-2.5 text-xs font-medium text-white hover:border-primary-400"
						:loading="isExporting"
						@click="handleExport"
					>
						<template #prefix>
							<tools-icon dir="system" icon="export" :height="16" />
						</template>
						Export
					</ui-button>

					<ui-button
						v-if="enableRefresh"
						variants="two"
						class="rounded-lg border border-gray-300 bg-white p-2.5 transition-all hover:border-primary-400 active:scale-95"
						@click="refreshRequest"
					>
						<tools-icon
							dir="line"
							icon="refresh"
							:height="20"
							:svg-class="isLoading ? 'animate-spin transition-all' : ''"
							path-class="fill-gray-400"
						/>
					</ui-button>
				</div>
			</slot>
		</div>

		<custom-table-loader :loading="isLoading" :columns="displayColumns">
			<template v-if="isInitiallyEmpty">
				<slot name="empty-cell">
					<ui-empty-state
						class="py-10"
						title="No Records"
						description="You don't have any records at the moment."
					>
						<template #icon>
							<tools-icon :height="120" :width="120" dir="fill" icon="invoice" />
						</template>
					</ui-empty-state>
				</slot>
			</template>

			<div v-else :class="tableClass">
				<a-table
					:key="tableKey"
					:columns="displayColumns"
					:data-source="sortedRecords"
					:row-selection="options?.rowSelection"
					:pagination="false"
					:scroll="scroll || { x: 'calc(100px + 60%)' }"
					class="ant-table-premium"
				>
					<template #headerCell="{ title, column }">
						<template v-if="getColKey(column) === 'checkbox'">
							<div class="flex items-center justify-center">
								<a-checkbox
									:checked="isAllSelected"
									:indeterminate="isIndeterminate"
									@change="e => handleToggleAllSelection(e.target.checked)"
								/>
							</div>
						</template>
						<div
							v-else
							role="button"
							tabindex="0"
							class="header-cell-outer group relative flex select-none items-center transition-all focus-within:ring-2 focus-within:ring-primary-500/50"
							:class="{
								'bg-primary-50 ring-2 ring-primary-500/30': dragOverIndex === getColIndex(column),
								'cursor-pointer hover:bg-gray-100': !immutableColumns.includes(getColKey(column))
							}"
							:style="
								(column as MinimalColumn).width
									? {
											minWidth:
												typeof (column as MinimalColumn).width === 'number'
													? `${(column as MinimalColumn).width}px`
													: String((column as MinimalColumn).width)
										}
									: undefined
							"
							:aria-label="`Sort by ${title || (column as MinimalColumn)?.title || (column as MinimalColumn)?.name}`"
							@dragover="
								!immutableColumns.includes(getColKey(column)) && onDragOver($event, getColIndex(column))
							"
							@dragleave="dragOverIndex = null"
							@drop="!immutableColumns.includes(getColKey(column)) && onDrop($event, getColIndex(column))"
							@click="handleSort(getColKey(column))"
							@keydown.enter="handleSort(getColKey(column))"
							@keydown.space.prevent="handleSort(getColKey(column))"
						>
							<div
								:draggable="enableDrag && !immutableColumns.includes(getColKey(column))"
								class="flex flex-1 items-center gap-1 py-4"
								@dragstart="onDragStart($event, getColIndex(column))"
							>
								<slot name="headerCell" v-bind="{ title, column }">
									<span class="whitespace-nowrap px-3 font-semibold text-gray-700">
										{{
											title || (column as MinimalColumn)?.title || (column as MinimalColumn)?.name
										}}
									</span>
								</slot>
								<tools-icon
									v-if="sortConfig.key === getColKey(column)"
									aria-hidden="true"
									dir="arrows"
									:icon="sortConfig.order === 'asc' ? 'arrow-up' : 'arrow-down'"
									:height="12"
									class="text-sky-500"
								/>
							</div>

							<div
								v-if="!immutableColumns.includes(getColKey(column))"
								class="resize-handle absolute bottom-0 right-0 top-0 z-20 w-[4px] cursor-col-resize transition-all hover:bg-primary-400 active:bg-primary-600"
								@mousedown.stop="startResize($event, getColIndex(column))"
								@click.stop
							/>
						</div>
					</template>

					<template #bodyCell="cellProps">
						<span v-if="getColKey(cellProps.column) === 'sn'" class="font-variant-numeric tabular-nums">
							{{ format((cellProps.index as number) + 1 + (currentPage - 1) * limit) }}
						</span>

						<div
							v-else-if="getColKey(cellProps.column) === 'checkbox'"
							class="flex items-center justify-center"
						>
							<a-checkbox
								:checked="isRecordSelected(cellProps.record as T)"
								@change="e => handleToggleSelection(cellProps.record as T, e.target.checked)"
							/>
						</div>

						<slot
							v-else
							name="bodyCell"
							:column="cellProps.column"
							:text="cellProps.text"
							:value="cellProps.value"
							:record="list(cellProps)"
							:index="cellProps.index"
						/>
					</template>

					<template #emptyText>
						<div class="py-16">
							<slot name="empty">
								<ui-empty-state
									title-class="text-gray-400"
									:title="
										options?.emptyTextPlaceholder ||
										((options?.filter as RecordSchema)?.search
											? `No results for '${(options.filter as RecordSchema).search}'`
											: 'No Records Found')
									"
									class="space-y-4 py-12"
								>
									<template #icon>
										<tools-icon
											dir="fill"
											icon="mcg-cap-lg-gray"
											class="cursor-pointer"
											svg-class="md:h-[120px] md:w-[120px] h-[80px] w-[80px]"
											path-class="fill-gray-300"
										/>
									</template>
								</ui-empty-state>
							</slot>
						</div>
					</template>
				</a-table>
			</div>
		</custom-table-loader>

		<div
			class="flex flex-wrap-reverse items-center gap-5 border-t border-gray-100 bg-white px-5 py-4"
			:class="[options?.paginationWrapperClass ?? 'mt-2', paginationJustify]"
		>
			<custom-table-pagination-limit
				v-show="showLimit"
				v-model="limit"
				:per-page="paginationQuery.limit"
				:total-pages="paginationMeta.total_pages"
			/>

			<custom-table-pagination
				v-if="showPagination"
				class="overflow-x-auto"
				:current-page="currentPage"
				:total-items="paginationMeta.total_pages"
				:per-page="Number(paginationQuery.limit || 0)"
				@page-changed="
					(page: number) => {
						switchPages(page)
						emit('pageChange', page)
					}
				"
			/>
		</div>
	</div>
</template>

<style scoped>
	.ant-table-premium :deep(.ant-table-thead .ant-table-cell) {
		@apply border-b border-gray-100 bg-gray-100 px-0 py-0 font-semibold text-gray-600;
	}

	.ant-table-premium :deep(.ant-table-tbody .ant-table-cell) {
		@apply border-b border-gray-50 py-5;
	}

	.header-cell-outer {
		transition: background-color 0.2s ease;
	}

	.resize-handle {
		opacity: 0;
	}

	.header-cell-outer:hover .resize-handle {
		opacity: 1;
	}

	.ant-table-premium :deep(.ant-table-thead .ant-table-cell:last-child),
	.ant-table-premium :deep(.ant-table-tbody .ant-table-cell:last-child) {
		@apply sticky right-0 z-10 bg-inherit shadow-none;
	}
	.ant-table-premium :deep(.ant-table-thead .ant-table-cell:last-child) {
		@apply bg-gray-50;
	}
	.ant-table-premium :deep(.ant-table-tbody .ant-table-cell:last-child) {
		@apply bg-white;
	}

	.ant-table-premium :deep(.ant-table-cell-fix-left-last::after),
	.ant-table-premium :deep(.ant-table-cell-fix-right-first::after) {
		display: none !important;
	}
</style>
